import { photosApi } from '../api/photos';
import { useUploadStore, UploadTask } from '../store/uploadStore';
import { Photo } from '../types';

// File objects - not serializable, kept outside Zustand
const fileMap = new Map<string, File>();

// Per-file progress subscribers. Both the UploadModal file rows and the
// gallery's uploading cards subscribe; each writes the percentage straight to
// its own DOM refs so progress ticks never trigger React re-renders.
type ProgressCb = (id: string, pct: number) => void;
const _progressCbs = new Set<ProgressCb>();
let _abortController: AbortController | null = null;

const CONCURRENCY = 8;

/** The pending File for an upload task, if still held (cleared once uploaded). */
export function getUploadFile(id: string): File | undefined {
  return fileMap.get(id);
}

// Stage a single file to SW IndexedDB just before its upload begins.
// Called inside the concurrentMap callback, so at most CONCURRENCY files
// are ever in IDB simultaneously (~CONCURRENCY × avg_file_size peak).
async function stageFileToSW(task: UploadTask, file: File, sw: ServiceWorker): Promise<void> {
  try {
    const data = await file.arrayBuffer(); // read from disk → heap
    sw.postMessage(                         // transfer ownership → SW; heap freed here
      { action: 'ENQUEUE', id: task.id, filename: task.filename, size: task.size, eventId: task.eventId, data },
      [data],
    );
  } catch {
    // Best-effort — main-thread upload continues without IDB staging
  }
}

function getStore() {
  return useUploadStore.getState();
}

async function getSWController(): Promise<ServiceWorker | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return reg.active;
  } catch {
    return null;
  }
}

function concurrentMap<T>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<void>,
): Promise<void> {
  return new Promise((resolve) => {
    if (items.length === 0) { resolve(); return; }
    let idx = 0, running = 0, completed = 0;
    function next() {
      while (running < concurrency && idx < items.length) {
        const item = items[idx++];
        running++;
        fn(item).finally(() => {
          running--;
          completed++;
          if (completed === items.length) resolve();
          else next();
        });
      }
    }
    next();
  });
}

export const uploadManager = {
  /** Subscribe to per-file progress. Returns an unsubscribe function. */
  subscribeProgress(cb: ProgressCb): () => void {
    _progressCbs.add(cb);
    return () => { _progressCbs.delete(cb); };
  },

  addFiles(eventId: string, files: File[]): UploadTask[] {
    const tasks: UploadTask[] = files.map((file) => {
      const id = crypto.randomUUID();
      fileMap.set(id, file);
      // previewUrl is filled in lazily by uploadThumbnails as rows scroll into view.
      return { id, filename: file.name, size: file.size, eventId, status: 'queued' as const };
    });
    getStore().addTasks(tasks);
    return tasks;
  },

  async startUpload(onPhotosReady: (photos: Photo[]) => void): Promise<void> {
    const { tasks, isUploading, setUploading, updateTask } = getStore();
    const pending = tasks.filter((t) => t.status === 'queued');
    if (pending.length === 0 || isUploading) return;

    setUploading(true);
    _abortController = new AbortController();
    const { signal } = _abortController;
    const succeeded: Photo[] = [];
    const sw = await getSWController();

    await concurrentMap(pending, CONCURRENCY, async (task) => {
      if (signal.aborted) return;
      updateTask(task.id, { status: 'uploading' });
      const file = fileMap.get(task.id);
      if (!file) {
        updateTask(task.id, { status: 'failed', error: 'File not available' });
        return;
      }

      // Stage to SW IDB just before upload — if the tab closes mid-upload the
      // SW can retry this file. At most CONCURRENCY files are in IDB at once.
      if (sw) await stageFileToSW(task, file, sw);

      try {
        const result = await photosApi.uploadSingle(task.eventId, file, (pct) => {
          _progressCbs.forEach((cb) => cb(task.id, pct));
        }, signal);

        if (result?.status === 'success' && result.photo) {
          succeeded.push(result.photo);
          fileMap.delete(task.id);
          updateTask(task.id, { status: 'done', photo: result.photo });
          sw?.postMessage({ action: 'MARK_DONE', id: task.id });
          window.dispatchEvent(
            new CustomEvent('upload:photo-ready', { detail: { photo: result.photo, eventId: task.eventId } }),
          );
        } else {
          const error = result?.error ?? 'Upload failed';
          fileMap.delete(task.id);
          updateTask(task.id, { status: 'failed', error });
          sw?.postMessage({ action: 'MARK_FAILED', id: task.id, error });
        }
      } catch (err: unknown) {
        // Swallow abort errors — cancelAll() already cleaned up the store
        if (signal.aborted) return;
        const msg =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          (err as Error)?.message ??
          'Upload failed';
        updateTask(task.id, { status: 'failed', error: msg });
        sw?.postMessage({ action: 'MARK_FAILED', id: task.id, error: msg });
      }
    });

    _abortController = null;
    setUploading(false);
    if (succeeded.length > 0) onPhotosReady(succeeded);
  },

  cancelAll() {
    _abortController?.abort();
    _abortController = null;
    getStore().clearAll();
  },

  /**
   * Discard files that are queued but not yet uploading for an event, freeing
   * their held File objects. In-progress and settled tasks are left untouched.
   * Queued files are not yet staged to the SW IDB, so no SW cleanup is needed.
   */
  discardQueued(eventId: string) {
    getStore().tasks
      .filter((t) => t.eventId === eventId && t.status === 'queued')
      .forEach((t) => fileMap.delete(t.id));
    getStore().clearQueuedFor(eventId);
  },

  clearCompleted(eventId: string) {
    getStore().clearDoneFor(eventId);
    getSWController().then((sw) => sw?.postMessage({ action: 'CLEAR_DONE' }));
  },

  get isActive(): boolean {
    const { tasks, isUploading } = getStore();
    return isUploading || tasks.some((t) => t.status === 'queued' || t.status === 'uploading');
  },
};

// ── SW message handler (SW → main thread) ────────────────────────────
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    const msg = event.data as {
      type: string;
      id?: string;
      status?: string;
      error?: string;
      photo?: import('../types').Photo;
      eventId?: string;
      tasks?: Array<{ id: string; filename: string; size: number; eventId: string; status: string; error?: string; photo?: import('../types').Photo }>;
    };

    if (msg.type === 'TASK_STATUS' && msg.id) {
      getStore().updateTask(msg.id, {
        status: msg.status as import('../store/uploadStore').UploadTaskStatus,
        ...(msg.error ? { error: msg.error } : {}),
        ...(msg.photo ? { photo: msg.photo } : {}),
      });
      if (msg.status === 'done' && msg.photo) {
        window.dispatchEvent(
          new CustomEvent('upload:photo-ready', { detail: { photo: msg.photo, eventId: msg.eventId } }),
        );
      }
    }

    // SW sends task summaries on page load (GET_QUEUE response)
    if (msg.type === 'QUEUE_STATUS' && msg.tasks) {
      const existing = new Set(getStore().tasks.map((t) => t.id));
      const recovered = msg.tasks.filter((t) => !existing.has(t.id) && t.status === 'queued');
      if (recovered.length > 0) {
        getStore().addTasks(
          recovered.map((t) => ({
            id: t.id,
            filename: t.filename,
            size: t.size,
            eventId: t.eventId,
            status: 'queued' as const,
          })),
        );
      }
    }
  });

  // Queued files are NOT yet in IDB — closing the tab would lose them.
  // Files with status 'uploading' are already staged to IDB and the SW can retry them.
  window.addEventListener('beforeunload', (e) => {
    const hasQueued = getStore().tasks.some((t) => t.status === 'queued');
    if (hasQueued) {
      e.preventDefault();
      e.returnValue = '';
    }
  });
}

export function registerUploadSW(): void {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/upload-sw.js', { scope: '/' }).catch(console.error);
      // Check SW for tasks from a previous session
      navigator.serviceWorker.ready.then((reg) => {
        reg.active?.postMessage({ action: 'GET_QUEUE' });
      });
    });
  }
}
